// Context object + type + hook for SubtitlesContext, split out of the .tsx so the
// component file only exports the SubtitlesProvider component
// (react-refresh/only-export-components).

import { createContext, useContext } from 'react';
import type { SubtitlesState, SubtitleOutputMode, SubtitleFormat, SubtitleStyle } from './types';
import type { MediaType } from '@/components/common/MediaUploader';
import type { SubtitlesStateReturn } from './hooks/useSubtitlesState';
import type { UseSubtitlesApiReturn } from './hooks/useSubtitlesApi';

export interface SubtitlesContextValue {
  state: SubtitlesState;
  actions: SubtitlesStateReturn['actions'];
  // Derived flags
  isUploading: boolean;
  isGenerating: boolean;
  isCompleted: boolean;
  showForm: boolean;
  // API handlers
  handleMediaSelect: (file: File, type: MediaType) => void;
  handleMediaRemove: () => void;
  handleSourceLanguageChange: (language: string) => void;
  handleTargetLanguagesChange: (languages: string[]) => void;
  handleOutputModeChange: (mode: SubtitleOutputMode) => void;
  handleFormatsChange: (formats: SubtitleFormat[]) => void;
  handleLlmRefinementChange: (enabled: boolean) => void;
  handleKaraokeChange: (enabled: boolean) => void;
  handleStyleChange: (style: SubtitleStyle) => void;
  handleHardBurnConfirmedChange: (confirmed: boolean) => void;
  handleGenerate: () => Promise<void>;
  handleCancel: () => Promise<void>;
  handleReset: () => void;
  getDownloadUrl: UseSubtitlesApiReturn['getDownloadUrl'];
}

export const SubtitlesContext = createContext<SubtitlesContextValue | null>(null);

export function useSubtitles(): SubtitlesContextValue {
  const ctx = useContext(SubtitlesContext);
  if (ctx === null) {
    throw new Error('useSubtitles must be used within a SubtitlesProvider');
  }
  return ctx;
}
